import React, { useState, useEffect } from 'react';
import {
    Container,
    Card,
    CardContent,
    Stepper,
    Step,
    StepLabel,
    Button,
    Box,
    Typography,
    Alert,
} from '@mui/material';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import {
    ocrCustomerInfo,
    requestEmailOTP,
    createCustomer,
    extractOrgIdFromToken,
} from '../services/api';
import { getQueryParam } from '../utils/helpers';
import StepUploadDocuments from '../components/registration/StepUploadDocuments';
import StepVerifyInfo from '../components/registration/StepVerifyInfo';
import StepOTPVerification from '../components/registration/StepOTPVerification';
import StepSuccess from '../components/registration/StepSuccess';

const steps = ['Giấy Tờ', 'Thông Tin', 'OTP', 'Hoàn Tất'];

const RegistrationPage = () => {

    const [activeStep, setActiveStep] = useState(0);
    const [qrToken, setQrToken] = useState('');
    const [orgId, setOrgId] = useState('');
    const [ignoreNameMatch, setIgnoreNameMatch] = useState(false);

    // Step 1 state
    const [images, setImages] = useState([]);
    const [ocrResponse, setOcrResponse] = useState(null);
    const [step1Loading, setStep1Loading] = useState(false);
    const [step1Error, setStep1Error] = useState('');
    const [nameWarning, setNameWarning] = useState('');

    // Step 2 state
    const [formData, setFormData] = useState({
        fullName: '',
        idNumber: '',
        address: '',
        phoneNumber: '',
        email: '',
        employeeCode: '',
        dateOfBirth: '',
        gender: '',
    });
    const [step2Loading, setStep2Loading] = useState(false);
    const [step2Error, setStep2Error] = useState('');
    const [otpSent, setOtpSent] = useState(false);
    const [otpExpiresAt, setOtpExpiresAt] = useState('');

    // Step 3 state
    const [otp, setOtp] = useState('');
    const [step3Loading, setStep3Loading] = useState(false);
    const [step3Error, setStep3Error] = useState('');

    // Step 4 state
    const [customerData, setCustomerData] = useState(null);

    // Get QR token from URL on mount
    useEffect(() => {
        const token = getQueryParam('token');
        const ignoreMatch = getQueryParam('ignore-match-fullname');

        // Default token for testing/fallback
        const DEFAULT_TOKEN = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJmb3JtVXJsIjoiaHR0cHM6Ly9sb3lhbHR5LmRhaW1pbmhwaGF0LmNvbS52bi9wcmlvcml0eS1yZWdpc3RlciIsIm9yZ0lkIjoiNjc4MmEwYzk1OTczM2VjNTZiZGMwYmIwIiwiaWF0IjoxNzcwOTUyODY0LCJleHAiOjE3NzM1NDQ4NjR9.oQp1nBGj5XkHHahMyowIdpZiLLpUqA4NInkURcL97FE';

        if (token && token.trim() !== '') {
            setQrToken(token);
            const extractedOrgId = extractOrgIdFromToken(token);
            setOrgId(extractedOrgId || process.env.REACT_APP_ORG_ID);
        } else {
            // Use default token when missing or empty
            setQrToken(DEFAULT_TOKEN);
            const extractedOrgId = extractOrgIdFromToken(DEFAULT_TOKEN);
            setOrgId(extractedOrgId || process.env.REACT_APP_ORG_ID);
        }

        // Check if should ignore name matching (for testing)
        if (ignoreMatch === 'true') {
            setIgnoreNameMatch(true);
        }
    }, []);

    // Helper: Normalize name for comparison
    const normalizeName = (name) => {
        return name
            .toUpperCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/\s+/g, ' ')
            .trim();
    };

    // Helper: Check if names match across documents
    const checkNameConsistency = (documents) => {
        const names = [];

        documents.forEach((doc) => {
            if (doc.fields?.fullName?.value) {
                names.push(normalizeName(doc.fields.fullName.value));
            }
        });

        if (names.length < 2) return true; // Only one document, no conflict

        const firstName = names[0];
        const allMatch = names.every((name) => name === firstName);

        return allMatch;
    };

    // Step 1: OCR
    const handleOCR = async () => {
        setStep1Loading(true);
        setStep1Error('');
        setNameWarning('');

        try {
            const imageBase64Array = images.map((img) => img.base64);
            const data = await ocrCustomerInfo(imageBase64Array);

            setOcrResponse(data);

            // Check name consistency (skip if ignoreNameMatch is true)
            if (!ignoreNameMatch && data.documents && data.documents.length > 1) {
                const namesMatch = checkNameConsistency(data.documents);
                if (!namesMatch) {
                    setNameWarning(
                        'Cảnh báo: Họ tên trên các giấy tờ không khớp nhau. Vui lòng kiểm tra lại.'
                    );
                }
            }

            // Auto-fill form data
            setFormData({
                fullName: data.fullName || '',
                idNumber: data.idNumber || '',
                address: data.address || '',
                phoneNumber: data.phoneNumber || '',
                email: data.email || '',
                employeeCode: data.employeeCode || '',
                dateOfBirth: data.dateOfBirth || '',
                gender: data.gender || '',
            });

            // Move to next step
            setActiveStep(1);
        } catch (err) {
            setStep1Error(err.message);
        } finally {
            setStep1Loading(false);
        }
    };

    // Step 2: Request OTP
    const handleRequestOTP = async () => {
        // Block if name warning exists (unless ignoreNameMatch is true)
        if (!ignoreNameMatch && nameWarning) {
            setStep2Error('Không thể tiếp tục do họ tên trên các giấy tờ không khớp nhau.');
            return;
        }

        setStep2Loading(true);
        setStep2Error('');

        try {
            const data = await requestEmailOTP(formData.email, formData.idNumber, orgId);
            setOtpExpiresAt(data.otpExpiresAt);
            setOtpSent(true);

            // Auto-advance to Step 3 after 1 second
            setTimeout(() => {
                setActiveStep(2);
            }, 1000);
        } catch (err) {
            setStep2Error(err.message);
        } finally {
            setStep2Loading(false);
        }
    };

    // Step 3: Submit Registration
    const handleSubmitRegistration = async () => {
        setStep3Loading(true);
        setStep3Error('');

        try {
            // Prepare images object based on OCR response
            const imagesObj = {};

            if (ocrResponse?.documents) {
                ocrResponse.documents.forEach((doc, index) => {
                    if (doc.type === 'cccd' && images[index]) {
                        imagesObj.cccd = images[index].base64;
                    } else if (doc.type === 'employee_card' && images[index]) {
                        imagesObj.employeeCard = images[index].base64;
                    }
                });
            }

            // Fallback: if no documents matched, use first two images
            if (!imagesObj.cccd && images[0]) {
                imagesObj.cccd = images[0].base64;
            }
            if (!imagesObj.employeeCard && images[1]) {
                imagesObj.employeeCard = images[1].base64;
            }

            const payload = {
                ...formData,
                emailOtp: otp,
                qrToken: qrToken,
                images: imagesObj,
            };

            const data = await createCustomer(payload);
            setCustomerData(data.customer);

            // Move to success step
            setActiveStep(3);
        } catch (err) {
            setStep3Error(err.message);
        } finally {
            setStep3Loading(false);
        }
    };

    // Resend OTP
    const handleResendOTP = async () => {
        setStep3Error('');
        try {
            const data = await requestEmailOTP(formData.email, formData.idNumber, orgId);
            setOtpExpiresAt(data.otpExpiresAt);
            setOtp(''); // Clear OTP input
        } catch (err) {
            setStep3Error(err.message);
        }
    };

    // Handle back button
    const handleBack = () => {
        if (activeStep === 2 && otpSent) {
            // Allow going back to edit info
            setOtpSent(false);
        }
        setActiveStep((prev) => prev - 1);
    };

    // Reset form
    const handleReset = () => {
        setActiveStep(0);
        setImages([]);
        setOcrResponse(null);
        setNameWarning('');
        setFormData({
            fullName: '',
            idNumber: '',
            address: '',
            phoneNumber: '',
            email: '',
            employeeCode: '',
            dateOfBirth: '',
            gender: '',
        });
        setOtp('');
        setOtpSent(false);
        setCustomerData(null);
    };

    const getStepContent = (step) => {
        switch (step) {
            case 0:
                return (
                    <StepUploadDocuments
                        images={images}
                        setImages={setImages}
                        onOCR={handleOCR}
                        loading={step1Loading}
                        error={step1Error}
                    />
                );
            case 1:
                return (
                    <Box>
                        {nameWarning && (
                            <Alert severity="warning" sx={{ mb: 3 }}>
                                {nameWarning}
                            </Alert>
                        )}
                        <StepVerifyInfo
                            formData={formData}
                            setFormData={setFormData}
                            onRequestOTP={handleRequestOTP}
                            loading={step2Loading}
                            error={step2Error}
                            otpSent={otpSent}
                        />
                    </Box>
                );
            case 2:
                return (
                    <StepOTPVerification
                        otp={otp}
                        setOtp={setOtp}
                        otpExpiresAt={otpExpiresAt}
                        onSubmit={handleSubmitRegistration}
                        onResendOTP={handleResendOTP}
                        loading={step3Loading}
                        error={step3Error}
                    />
                );
            case 3:
                return <StepSuccess customerData={customerData} onClose={handleReset} />;
            default:
                return null;
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'background.default',
            }}
        >
            <Container maxWidth="md" sx={{ flex: 1, py: 4 }}>
                <Card elevation={3}>
                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                        <Typography variant="h5" align="center" color="primary" sx={{ mb: 1 }}>
                            Đăng Ký Khách Hàng Ưu Đãi
                        </Typography>

                        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
                            Hoàn thành các bước để trở thành khách hàng ưu đãi
                        </Typography>

                        {/* Stepper - Always Horizontal */}
                        <Stepper
                            activeStep={activeStep}
                            orientation="horizontal"
                            sx={{ mb: 2 }}
                        >
                            {steps.map((label) => (
                                <Step key={label}>
                                    <StepLabel>{label}</StepLabel>
                                </Step>
                            ))}
                        </Stepper>

                        {/* Step Content */}
                        <Box>{getStepContent(activeStep)}</Box>

                        {/* Navigation Buttons */}
                        {activeStep < 3 && activeStep > 0 && !otpSent && (
                            <Box sx={{ mt: 2 }}>
                                <Button
                                    startIcon={<BackIcon />}
                                    onClick={handleBack}
                                    disabled={step1Loading || step2Loading || step3Loading}
                                >
                                    Quay lại
                                </Button>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            </Container>

            {/* Footer */}
            <Box
                component="footer"
                sx={{
                    py: 3,
                    textAlign: 'center',
                }}
            >
                <Typography variant="body2" color="text.secondary">
                    © Dai Minh Phat
                </Typography>
            </Box>
        </Box>
    );
};

export default RegistrationPage;
